import type { LobbyTableInfo } from '@draughts/shared';
import { parseTimeControl } from './cadence-icons';
import type { RatingBucket } from './rating-bucket';

/**
 * Chi, fra i collegati, ha un tavolo aperto in lobby.
 *
 * La lista dei collegati e la lista dei tavoli arrivano da due porte diverse e
 * non si conoscono: `PublicOnlineUser` porta lo `userId`, `LobbyTableInfo`
 * dichiara di NON portare user id — e' in broadcast a tutti, ospiti compresi, e
 * c'e' un test del server che lo sorveglia. L'unica cosa che le due hanno in
 * comune e' il nome, che in DB e' unico e senza distinzione di maiuscole
 * (migrazione 019), quindi e' lui la chiave.
 *
 * Prezzo dichiarato: un host che si rinomina DOPO aver aperto il tavolo porta
 * con se' il nome vecchio dentro `hostUsername` fino alla scadenza, e per quel
 * tempo la sua riga non si accoppia. Costa un badge mancante, non un errore.
 */
export function mappaTavoliPerHost(
  tables: LobbyTableInfo[],
  ora: number = Date.now(),
): Map<string, LobbyTableInfo> {
  const perHost = new Map<string, LobbyTableInfo>();
  for (const table of tables) {
    // Un tavolo scaduto e' ancora nella lista del client: la spazzata del
    // server gira a intervalli, non all'istante giusto. Senza questo filtro un
    // tavolo morto dipinge il badge «In attesa» e offre un «Siediti» che il
    // server rifiuta.
    if (table.expiresAt <= ora) continue;
    if (!table.hostUsername) continue;
    perHost.set(table.hostUsername.toLowerCase(), table);
  }
  return perHost;
}

/** Il tavolo aperto di quel nome, se c'e'. */
export function tavoloDi(
  perHost: Map<string, LobbyTableInfo>,
  username: string,
): LobbyTableInfo | undefined {
  return perHost.get(username.toLowerCase());
}

/**
 * Quanto e' vicino questo tavolo a quello che si sta guardando.
 *
 * Chi apre il pannello ha una variante e una cadenza sotto gli occhi — quelle
 * della card dei punteggi, o del modulo di Sfida diretta — e sono le stesse che
 * la lista dichiara in testata. Un tavolo di un'altra variante e' comunque un
 * tavolo, ma in cima non ci va: era il difetto da chiudere, un frisone davanti
 * a un italiano mentre si guardava l'italiano.
 *
 * `0` variante e cadenza giuste, `1` solo la variante, `2` un'altra variante.
 * Senza bucket ritorna 1 per tutti: nessuna variante privilegiata, e i tavoli
 * restano fra loro nell'ordine in cui sono arrivati.
 *
 * Il confronto e' in minuscolo su tutti e due i lati per la ragione gia'
 * dichiarata in `LobbyTableList`: gli id delle varianti arrivano all'interfaccia
 * in due casature. La cadenza del tavolo la ricava `parseTimeControl` dal
 * prefisso del preset (`blitz-5+3` -> `blitz`), che e' la lettura unica; un
 * tavolo senza orologio non corrisponde a nessuna cadenza di punteggio.
 */
export function distanzaDalBucket(
  tavolo: LobbyTableInfo,
  bucket: RatingBucket | undefined,
): number {
  if (!bucket) return 1;
  if (String(tavolo.variant).toLowerCase() !== String(bucket.variant).toLowerCase()) return 2;
  return parseTimeControl(tavolo.timeControl)?.category === bucket.cadence ? 0 : 1;
}
